/* ==========================================================================
   Theme UI Core — Drawer Controller
   Web Component: <drawer-element>
   ========================================================================== */

if (!customElements.get('drawer-element')) {
    class DrawerElement extends HTMLElement {
        constructor() {
            super();
            this.drawer = null;
            this.overlay = null;
            this.panel = null;
            this.handle = null;
            this.previousFocus = null;
            this.focusableElements = [];

            // Drag state
            this._dragging = false;
            this._startY = 0;
            this._startX = 0;
            this._currentTranslate = 0;
            this._dismissThreshold = 0.3; // 30% of panel size to dismiss

            this._onPointerMove = this._onPointerMove.bind(this);
            this._onPointerUp = this._onPointerUp.bind(this);
        }

        connectedCallback() {
            this.drawer = this.querySelector('.drawer');
            this.overlay = this.querySelector('.drawer__overlay');
            this.panel = this.querySelector('.drawer__panel');
            this.handle = this.querySelector('.drawer__handle');

            if (!this.drawer || !this.panel) return;

            this._bindEvents();
            this._setupOpenTriggers();
        }

        disconnectedCallback() {
            document.removeEventListener('pointermove', this._onPointerMove);
            document.removeEventListener('pointerup', this._onPointerUp);
        }

        /* ------------------------------------------------------------------ */
        /*  Events                                                            */
        /* ------------------------------------------------------------------ */

        _bindEvents() {
            // Close via overlay click
            if (this.overlay) {
                this.overlay.addEventListener('click', () => this.close());
            }

            // Close buttons
            this.querySelectorAll('[data-action="close"]').forEach((el) => {
                el.addEventListener('click', () => this.close());
            });

            // ESC key
            this._onKeyDown = (e) => {
                if (e.key === 'Escape' && this.isOpen()) {
                    this.close();
                }
            };

            // Drag-to-dismiss via handle or panel
            if (this.handle) {
                this.handle.addEventListener('pointerdown', (e) => this._onPointerDown(e));
            }
        }

        _setupOpenTriggers() {
            const drawerId = this.dataset.drawerId || this.drawer?.id;
            if (!drawerId) return;

            document.querySelectorAll(`[data-drawer-open="${drawerId}"]`).forEach((trigger) => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.open();
                });
            });
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        isOpen() {
            return this.drawer?.classList.contains('drawer--active');
        }

        open() {
            if (!this.drawer || this.isOpen()) return;

            this.previousFocus = document.activeElement;
            this.drawer.classList.add('drawer--active');
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', this._onKeyDown);

            // Reset any drag transform
            this.panel.style.transform = '';
            this.panel.style.transition = '';

            // Trap focus
            requestAnimationFrame(() => {
                this.focusableElements = this.panel.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (this.focusableElements.length > 0) {
                    this.focusableElements[0].focus();
                }
            });

            this.dispatchEvent(
                new CustomEvent('theme:drawer:open', { bubbles: true, detail: { id: this.drawer.id } })
            );
        }

        close() {
            if (!this.drawer || !this.isOpen()) return;

            this.drawer.classList.remove('drawer--active');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this._onKeyDown);

            // Reset drag transform
            this.panel.style.transform = '';
            this.panel.style.transition = '';

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.dispatchEvent(
                new CustomEvent('theme:drawer:close', { bubbles: true, detail: { id: this.drawer.id } })
            );
        }

        /* ------------------------------------------------------------------ */
        /*  Drag-to-dismiss                                                   */
        /* ------------------------------------------------------------------ */

        _getSide() {
            return this.dataset.side || 'bottom';
        }

        _onPointerDown(e) {
            if (!this.isOpen()) return;
            e.preventDefault();

            this._dragging = true;
            this._startY = e.clientY;
            this._startX = e.clientX;
            this._currentTranslate = 0;
            this.panel.style.transition = 'none';

            document.addEventListener('pointermove', this._onPointerMove);
            document.addEventListener('pointerup', this._onPointerUp);
        }

        _onPointerMove(e) {
            if (!this._dragging) return;

            const side = this._getSide();
            let delta;

            switch (side) {
                case 'bottom':
                    delta = Math.max(0, e.clientY - this._startY);
                    this.panel.style.transform = `translateY(${delta}px)`;
                    break;
                case 'top':
                    delta = Math.min(0, e.clientY - this._startY);
                    this.panel.style.transform = `translateY(${delta}px)`;
                    break;
                case 'right':
                    delta = Math.max(0, e.clientX - this._startX);
                    this.panel.style.transform = `translateX(${delta}px)`;
                    break;
                case 'left':
                    delta = Math.min(0, e.clientX - this._startX);
                    this.panel.style.transform = `translateX(${delta}px)`;
                    break;
            }

            this._currentTranslate = Math.abs(delta);
        }

        _onPointerUp() {
            if (!this._dragging) return;
            this._dragging = false;

            document.removeEventListener('pointermove', this._onPointerMove);
            document.removeEventListener('pointerup', this._onPointerUp);

            const side = this._getSide();
            const isVertical = side === 'top' || side === 'bottom';
            const panelSize = isVertical ? this.panel.offsetHeight : this.panel.offsetWidth;

            this.panel.style.transition = '';

            if (this._currentTranslate > panelSize * this._dismissThreshold) {
                this.close();
            } else {
                // Snap back
                this.panel.style.transform = '';
            }
        }
    }

    customElements.define('drawer-element', DrawerElement);
}
